import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';

import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  finalize,
  of,
  Subscription,
  take,
  tap,
} from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { Moment } from 'moment-timezone';

import { ICapacity, Resource, ResourceAssignedToFloorplan } from '@shared/resource/models';
import { Schedule } from '@shared/schedule/models';
import { MergedCustomFields, Party } from '@shared/party/models';
import { Service } from '@shared/service/models';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { Restaurant } from '@shared/restaurant/models';
import { isUsOrCaPhoneNumber, useWhatsApp } from '@shared/sms/functions';
import { extractPartyFieldsFromCustomer } from '@shared/customer/functions';
import { isOnFreePlan } from '@shared/user/functions';
import { mergeCustomFields } from '@shared/party';
import {
  CommonRadioButtonItem,
  CustomDateParserFormatterProvider,
  CustomTimepickerModelAdapterProvider,
  DateTimeService,
  FloorplanResources,
  IParty,
  ProviderScheduleService,
  RESOURCE_ADJUSTMENT_ITEMS,
  TelService,
} from '@tablesready/common-ui';
import {
  DateService,
  MarkPartyService,
  PartiesService,
  WaitTimeService,
} from '../../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, ClockService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { CustomersService } from '../../../customers/services';

@Component({
  selector: 'tablesready-add-edit-party',
  templateUrl: './add-edit-party.component.html',
  styleUrls: ['./add-edit-party.component.scss'],
  providers: [
    CustomDateParserFormatterProvider,
    CustomTimepickerModelAdapterProvider,
  ],
})
export class AddEditPartyComponent implements OnInit, OnDestroy {
  @Input() partyToEdit: IParty;
  @Input() typeOfParty: PartyTypes;
  @Input() userSchedule;
  @Input() user: Restaurant;
  @Input() parties: IParty[];
  @Input() selectedServices;
  @Input() showSelectResourceMessage: boolean;
  @Input() selectedResource: Resource;
  @Input() selectedTime: number;
  @Input() selectedEndTime: number;
  @Input() selectedDate: NgbDateStruct;
  @Input() floorplanResources: FloorplanResources[];
  @Input() resourceOverrideCallback?: (party: IParty) => void;
  @Input() afterInit?: (instance: AddEditPartyComponent) => void;

  title: string;
  angularticsActionType: string;
  selectedTimeMode = false;
  isSubmitting = false;
  isSavingParty = false;
  now: number;
  resourcesInDropdown: ResourceAssignedToFloorplan[] = [];
  servicesInDropdown: Service[] = [];
  msPerMin = 60000;
  shortestPhoneDigits = 4;
  originalQuotedTime = 0;
  timezone: string;
  currentTime: Moment;
  isWorkTime = false;
  showResourcesSizeAlert = false;
  deltaResourcesSize = 0;
  determiner: 'less' | 'more' = 'more';
  determinerChange: 'decrease' | 'increase' = 'decrease';
  determinerFix: RESOURCE_ADJUSTMENT_ITEMS = RESOURCE_ADJUSTMENT_ITEMS.add;
  generalSchedule: Schedule[];
  overrideNoResources;
  exceptions;
  reservationMinutesPast = 420;
  sizeOther: number;
  icon = AppIcon;
  party;
  time;
  date;
  workHours: Array<[number, number]>;
  showOverlappingAlert;
  showResourceAlert = false;
  availablePartyDuration;
  availablePartyDurationInMin;
  removeServices = false;
  removeResources = false;
  showSizeValidationMessage;
  schedule;
  overrideIsWorkTime = false;
  // Date
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  noteOptions: string[] | string;
  serviceSettingsLabel: string;
  resourceSettingsLabel: string;
  displayOptionCustomerLabel: string;
  displayOptionsReservationsLabel: string;
  sizesRadioItems: CommonRadioButtonItem[] = [];
  servicesEnabled: boolean;
  partySources = PartySource;

  private overlappingParty: Party[];
  private allowMultipleParties: boolean;
  private resourcesEnabled: boolean;
  private onlineReservationEnabled: boolean;
  private partySizeForTimeSlots: boolean;
  private isEditMode = false;

  @ViewChild('confirmModalContent') confirmModalContentRef: ElementRef;
  @ViewChild('addEditForm') addEditForm: NgForm;

  private readonly subscription = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
    private readonly waitTimeService: WaitTimeService,
    private readonly dateService: DateService,
    private readonly partiesService: PartiesService,
    private readonly clockService: ClockService,
    private readonly telService: TelService,
    private readonly dateTimeService: DateTimeService,
    private readonly providerScheduleService: ProviderScheduleService,
    private readonly markPartyService: MarkPartyService,
    private readonly customerService: CustomersService,
  ) { }

  isOutOfBusinessHours(formGroup: NgForm): boolean {
    return (!this.isWorkTime || this.isPartyInPastTime) && this.isTimeFieldValid(formGroup);
  }

  isTimeFieldValid({ form: addEditForm }: NgForm): boolean {
    const time = addEditForm.get('time')?.value;

    return time !== '' && time !== undefined;
  }

  noAvailableServices(formGroup: NgForm): boolean {
    return this.servicesInDropdown.length === 0 && this.servicesEnabled &&
      this.isTimeFieldValid(formGroup) && !this.isOutOfBusinessHours(formGroup);
  }

  datepickerClick(datepicker: NgbInputDatepicker, dateInputRef: any): void {
    datepicker.open();
    setTimeout(() => {
      dateInputRef.focus();
    });
  }

  get isAvailableTime() {
    return this.isWorkTime &&
      this.showResourceAlert &&
      !this.resourcesInDropdown.length &&
      this.resourcesEnabled;
  }

  get isWalkin() {
    return this.typeOfParty === PartyTypes.walkIn;
  }

  get isReservation() {
    return this.typeOfParty === PartyTypes.reservation;
  }

  get isPartyInPastTime() {
    return this.currentTime.valueOf() < Date.now();
  }

  get isLocationOnFreePlan(): boolean {
    return isOnFreePlan({ account: this.user.account, location: this.user });
  }

  ngOnInit(): void {
    this.sizesRadioItems = [1, 2, 3, 4, 5, 6, 7].map((size) => ({
      name: String(size),
      value: size,
      onClick: () => this.sizeChanged(),
    }));

    this.subscription.add(this.clockService.clock$.subscribe(now => this.now = now));

    this.initModalProp();
    this.minDate = this.dateTimeService.momentToNgbDate(this.currentTime);
    this.maxDate = {
      ...this.minDate,
      year: this.minDate.year + 1,
    };
    if (this.partyToEdit) {
      this.initEditMode();
      this.checkWorkTime();
    } else if (this.selectedTime && this.selectedResource) {
      this.initAddModeWithSelectedResourceAndTime(this.selectedTime);
    } else {
      this.initAddMode();
    }
    this.setTittle();

    if (this.afterInit) {
      this.afterInit(this);
    }
  }

  initModalProp() {
    this.noteOptions = this.user?.noteOptions || [];
    this.serviceSettingsLabel = this.user?.serviceSettings?.label || 'service';
    this.resourceSettingsLabel = this.user.resourceSettings?.label || 'resource';
    this.displayOptionCustomerLabel = this.user.displayOptions?.labels?.customer || 'customer';
    this.displayOptionsReservationsLabel = this.user.displayOptions?.labels?.reservation || 'reservation';
    this.allowMultipleParties = this.user?.bookingOptions?.allowMultipleParties;
    this.servicesEnabled = this.user?.serviceSettings?.enabled;
    this.resourcesEnabled = this.user?.resourceSettings?.enabled;
    this.onlineReservationEnabled = this.user?.bookingOptions?.onlineReservationsEnabled;
    this.partySizeForTimeSlots = this.user?.bookingOptions?.partySizeForTimeSlots;

    this.generalSchedule = (this.userSchedule || []).filter(schedule => schedule.default);

    try {
      this.schedule = this.generalSchedule[0].hours;
    } catch (e) {
      this.alertsService.add({
        type: IAlertTypes.danger,
        heading: 'Please fill in work schedule in Settings -> Schedules',
        body: '',
      });
    }

    this.timezone = this.user.timezone;
    this.currentTime = moment.tz(this.timezone);
    this.exceptions = this.generalSchedule[0]?.exceptions || [];
    this.showSelectResourceMessage = this.showSelectResourceMessage || false;
    this.overrideNoResources = !(this.user.serviceSettings?.enabled || this.user.bookingOptions?.onlineReservationsEnabled);
  }

  setTittle() {
    this.title += this.typeOfParty === PartyTypes.reservation ? ` ${this.user.displayOptions?.labels?.reservation || 'reservation'}` : 'to Waitlist';
  }

  initEditMode() {
    this.title = 'Edit ';
    this.angularticsActionType = 'edited reservation';
    this.party = cloneDeep(this.partyToEdit);
    this.selectedTimeMode = false;
    this.isEditMode = true;
    const quotedTime = moment.tz(this.party.quoted_time, this.timezone);
    this.setPartyDurationInMinutes();
    this.party.phone = this.telService.format(this.party.phone, this.user.country, 'custom');
    this.party.quoted_time_minutes = this.typeOfParty === PartyTypes.walkIn ? Math.round((this.party.quoted_time - this.now) / this.msPerMin) : 0;
    this.party.date = this.dateTimeService.momentToNgbDate(quotedTime);
    this.party.time = quotedTime.format('h:mm A');
    this.originalQuotedTime = this.party.quoted_time;
    this.currentTime = moment(this.party.quoted_time).tz(this.timezone);
    this.setPartySize(this.party.size);
    this.isWorkTime = true;
  }

  setPartySize(size) {
    if (size < 8) {
      this.party.size = size;

      return true;
    }
    this.sizeOther = this.party.size;
  }

  initAddModeWithSelectedResourceAndTime(time: number): void {
    this.title = 'Add ';
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = true;
    const selectedTime = this.dateTimeService.roundingMinutes(Number(time), 15, this.timezone);
    this.party = {
      quoted_time: this.typeOfParty === PartyTypes.reservation ? 0 : this.waitTimeService.getTime(),
      size: Math.max(this.selectedResource.capacity.min, Math.min(this.user.default_size, this.selectedResource.capacity.max)),
      type: this.getPartyType(this.typeOfParty),
      date: this.selectedDate,
      time: selectedTime.format('h:mm A'),
      resources: [this.selectedResource._id],
      services: [],
      customFields: {},
    };
    this.currentTime = selectedTime;
    this.checkWorkTime();
    this.setPartySize(this.party.size);
  }

  initAddMode() {
    this.title = 'Add ';
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = false;
    this.party = {
      quoted_time_minutes: this.typeOfParty === PartyTypes.reservation ? 0 : this.waitTimeService.getTime(),
      size: this.user.default_size,
      type: this.getPartyType(this.typeOfParty),
      date: this.selectedDate,
      customFields: {},
    };
    this.party.quoted_time = this.currentTime.add(this.party.quoted_time_minutes, 'minutes');
    this.checkWorkTime();
    this.setPartySize(this.party.size);
  }

  getPartyType(typeOfParty: PartyTypes): PartyTypes {
    return typeOfParty === PartyTypes.reservation ? PartyTypes.reservation : PartyTypes.walkIn;
  }

  checkWorkTime(): boolean {
    if (!this.servicesEnabled && this.resourcesEnabled) {
      this.getAvailableResourcesServices(this.currentTime.format('x'), this.party);
    }
    if (this.typeOfParty === PartyTypes.reservation && !this.party.time) {
      if (this.resourceOverrideCallback) {
        this.resourceOverrideCallback(this.party);
      }

      return false;
    }
    this.isSubmitting = true;
    this.time = this.party.time || this.currentTime.format('h:mm A');
    this.date = this.currentTime.format('D-M-YYYY');

    const { hours, exceptions } = this.generalSchedule[0];
    this.workHours = this.providerScheduleService
      .getWorkingHours(hours, exceptions, this.currentTime.valueOf(), this.timezone);

    if (this.party.duration <= 0) {
      this.party.duration = this.dateService.checkServicesDuration(this.servicesInDropdown, this.party.services);
    }

    this.setPartyDurationInMinutes();

    this.isWorkTime = this.providerScheduleService
      .comparisonTime(this.currentTime.valueOf(), this.workHours, this.party.duration);
    if (this.servicesEnabled || this.resourcesEnabled) {
      this.getAvailableResourcesServices(this.currentTime.format('x'), this.party);
    } else {
      this.isSubmitting = false;
    }
  }

  getAvailableResourcesServices(date, party) {
    const { services, resources, size, duration } = party;
    if (!date && !services && !resources) {
      return false;
    }

    const type = party.type === PartyTypes.reservation ? party.type : PartyTypes.walkIn;
    const dateMilliseconds = this.dateTimeService.ngbDateToMoment(this.party.date, this.timezone).valueOf();
    const { hours, exceptions } = this.generalSchedule[0];
    const hoursWithDate = this.providerScheduleService.getWorkingHours(
      hours,
      exceptions,
      dateMilliseconds,
      this.timezone,
    );

    this.partiesService.findResourcesServicesForNewParty(
      date,
      type,
      services,
      resources,
      size,
      duration,
      party._id,
      this.overrideNoResources,
      hoursWithDate,
    )
      .pipe(
        take(1),
        tap((dataInput) => {
          const data = dataInput as any;

          let resourcesCapacity: ICapacity = {
            min: 0,
            max: 0,
          };
          this.showOverlappingAlert = false;
          this.overlappingParty = data?.overlappingParty;

          if (data?.availableResources) {
            this.resourcesInDropdown = data.availableResources
              .filter(resource => size >= resource.capacity.min);
            // If party.resource is already chosen and do not belong to resources list we need clear it
            if (this.resourcesInDropdown.length && party.resources) {
              party.resources = party.resources.filter((resourceId) => {
                const is = this.resourcesInDropdown.find(resource => resource._id === resourceId);

                return Boolean(is);
              });
            }

            if (this.resourcesEnabled) {
              this.showResourceAlert = true;
            }

            resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
          }

          if (data?.availableServices) {
            this.servicesInDropdown = data.availableServices;
          }

          if (!this.overlappingParty) {
            this.overlappingParty = [];
          }

          if (this.overlappingParty.length && this.onlineReservationEnabled) {
            if (this.allowMultipleParties || this.overrideNoResources) {
              this.showSizeAlert(resourcesCapacity);
            } else {
              this.showOverlappingAlert = true;
              const overlappingParty = this.overlappingParty[0];
              this.availablePartyDuration = overlappingParty.quoted_time - date;
              this.availablePartyDurationInMin = Math.round(this.availablePartyDuration / this.msPerMin);
            }
          }

          if (this.servicesInDropdown?.length === 1 && !this.party?.services?.length && !this.removeServices) {
            const serviceIdToSelect = this.servicesInDropdown[0]._id;
            this.party.services = [serviceIdToSelect];

            if (this.user.resourceSettings?.enabled && this.resourcesInDropdown.length && !this.removeResources && !this.selectedResource) {
              let resourceIdToSelect: string;

              for (const availableResource of data?.availableResources) {
                if (availableResource.services.includes(serviceIdToSelect) &&
                  availableResource.capacity.max >= size) {
                  // We don't want to pre-select a resource that doesn't fit party size
                  // And we've already filtered resources by min capacity
                  resourceIdToSelect = availableResource._id;

                  break;
                }
              }

              if (resourceIdToSelect !== undefined) {
                this.party.resources = [resourceIdToSelect];
                this.selectResources();
              }
            }
            this.selectServices();
            if (this.user.resourceSettings?.enabled && this.party?.resources?.length) {
              this.selectResources();
            }
          }
          this.isSubmitting = false;

          if (this.resourceOverrideCallback) {
            this.resourceOverrideCallback(this.party);
          }
        }),
        catchError((error) => {
          if (error.status === 401) {
            this.cancel();
          } else {
            this.alertsService.add({
              type: IAlertTypes.danger,
              heading: 'Error:',
              body: `resources and services not found: ${error}`,
            });
          }

          return of(error);
        }),
      )
      .subscribe();
  }

  calculateCapacityInSelectedResources(resources = [], selectedResources = []) {
    const fullDataOfSelectedResources = resources.filter(resource => selectedResources.includes(resource._id));

    return this.calculateResourcesCapacity(fullDataOfSelectedResources);
  }

  calculateResourcesCapacity(resources = []) {
    return resources.reduce((acc, resource) => {
      acc.min += resource.capacity.min || 0;
      acc.max += resource.capacity.max || 0;

      return acc;
    }, {
      min: 0,
      max: 0,
    });
  }

  selectServices() {
    if (this.party.services && this.party.services.length === 0) {
      // Reset errors if services removed from party
      this.removeServices = true;
      this.showResourceAlert = false;
      this.checkWorkTime();

      return;
    }
    this.removeServices = false;
    if (this.resourcesEnabled) {
      this.showResourceAlert = true;
    }
    this.party.duration = 0;
    this.checkWorkTime();
  }

  doesEditingHaveAnyConflicts(): Record<string, boolean> {
    return {
      editFormInvalid: this.addEditForm.form.invalid,
      isOutOutBusinessHours: (!this.isWorkTime || this.isPartyInPastTime) && !this.overrideIsWorkTime,
      isAvailableTimeAndNoOverride: this.isAvailableTime && !this.overrideNoResources,
      noAvailService: this.noAvailableServices(this.addEditForm),
      noConfirm: this.isConfirmPartyButtonDisabled(),
      formPending: this.addEditForm.form.pending,
      submitting: this.isSubmitting,
    };
  }

  addResourceToSelectedOnes(resourceId: string, services: string[]): void {
    if (this.party.resources?.length === 0) {
      this.party.resources = [resourceId];
      this.selectResources();

      return;
    }

    const doesExist = this.party?.resources?.findIndex((id) => id === resourceId);
    if (doesExist !== -1) {
      if (this.resourceOverrideCallback) {
        this.resourceOverrideCallback(this.party);
      }

      return;
    }

    this.party.resources = [...new Set([...this.party.resources, resourceId])];
    this.party.services = [...new Set([...this.party.services, ...services])];
    this.selectResources();
  }

  selectResources(): void {
    this.showResourceAlert = true;
    if ((this.party.resources || []).length) {
      this.removeResources = false;
      const resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, this.party.resources);
      this.showSizeAlert(resourcesCapacity);
    } else {
      this.removeResources = true;
    }
    this.checkWorkTime();
  }

  overrideResources() {
    this.checkWorkTime();
  }

  clearServicesResources() {
    this.party.services = [];
    this.party.resources = [];
  }

  setAvailableDuration() {
    this.party.duration = this.availablePartyDuration;
    this.party.durationInMinutes = this.availablePartyDurationInMin;
    this.showOverlappingAlert = false;
    this.availablePartyDuration = 0;
  }

  setQuotedTime() {
    this.currentTime = moment.tz(this.timezone);
    this.currentTime.add(this.party.quoted_time_minutes, 'minutes');
    this.clearServicesResources();
    this.checkWorkTime();
  }

  changeQuoted(num, model = 'quoted_time_minutes', modelToAddMilliseconds = '') {
    this.addEditForm.form.markAsDirty();
    this.party[model] += num;
    if (modelToAddMilliseconds) {
      this.party[modelToAddMilliseconds] += num * this.msPerMin;
    } else {
      this.clearServicesResources();
    }
    if (model === 'quoted_time_minutes') {
      this.currentTime.add(num, 'minutes');
    }
    this.checkWorkTime();
  }

  convertQuotedTime(party) {
    const resTime = moment.tz(this.timezone);

    delete party.date;
    delete party.time;
    delete party.durationInMinutes;
    delete party.quoted_time_minutes;
    delete party.className;
    delete party.assignedResources;

    if (this.party.type === PartyTypes.reservation && this.currentTime.format('x') < resTime.add(-this.reservationMinutesPast, 'minutes').format('x')) {
      party.quoted_time = Number(this.currentTime.format('x'));
      this.isWorkTime = false;

      return;
    }

    const updateWaitTime = !party._id && party.type === PartyTypes.walkIn; // Update wait time if new party
    party.quoted_time = party.quoted_time || 0;
    if (updateWaitTime && !(
      this.originalQuotedTime > 0 &&
      party.quoted_time > this.originalQuotedTime - this.msPerMin &&
      party.quoted_time < this.originalQuotedTime + this.msPerMin
    )) {
      this.waitTimeService.setTime(Math.round((party.quoted_time - this.now) / this.msPerMin));
    }
    party.quoted_time = Number(this.currentTime.format('x'));
  }

  showSizeAlert(resourcesCapacity: ICapacity): void {
    const isMoreCapacity = (this.party?.size > resourcesCapacity.max) || false;
    const isLessCapacity = (this.party?.size < resourcesCapacity.min) || false;

    if (this.overlappingParty && this.allowMultipleParties) {
      const partiesInTimeSlot = this.overlappingParty;
      let count = partiesInTimeSlot.length;
      let newPartySize = 1;

      if (this.partySizeForTimeSlots) {
        count = partiesInTimeSlot.reduce((sum, party) => sum + (party.size || 0), 0);
        newPartySize = this.party.size;
      }

      if ((count + newPartySize) > resourcesCapacity.max) {
        this.setDeterminerValues(count + newPartySize - resourcesCapacity.max);
        this.setShowResourcesSizeAlert();

        return;
      }
    }

    if (isLessCapacity || isMoreCapacity) {
      if (isLessCapacity) {
        this.setDeterminerValues(resourcesCapacity.min - this.party.size);
      }
      if (isMoreCapacity) {
        this.setDeterminerValues(this.party.size - resourcesCapacity.max);
      }
      this.setShowResourcesSizeAlert();
    } else {
      this.showResourcesSizeAlert = false;
    }
  }

  sizeChanged() {
    this.showSizeValidationMessage = false;
    this.showResourcesSizeAlert = false;
    const { resources, size } = this.party;
    if (this.user.resourceSettings?.enabled && this.party?.resources?.length) {
      const resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
      this.showSizeAlert(resourcesCapacity);
      this.sizeOther = size < 8 ? '' : size;

      return false;
    }
    this.checkWorkTime();
  }

  changeSize() {
    this.showSizeValidationMessage = false;
    this.party.size = this.sizeOther || this.user.default_size;
    this.sizeChanged();
  }

  partyPhoneLookup() {
    const { phone } = this.party;
    if (!phone ||
      phone === '' ||
      typeof phone === 'undefined' ||
      phone.length < this.shortestPhoneDigits
    ) {
      return;
    }
    const parsedPhone = this.telService.format(phone, this.user.country, 'intl');
    if (typeof parsedPhone !== 'undefined' && parsedPhone.trim().length >= this.shortestPhoneDigits) {
      this.customerService.getByPhone(parsedPhone)
        .pipe(
          tap(data => {
            if (data) {
              const customerCustomFields: MergedCustomFields = extractPartyFieldsFromCustomer(this.user.partyFields, data);
              const _data = {
                name: this.party.name || customerCustomFields.name,
                customFields: mergeCustomFields(this.party.customFields, customerCustomFields.customFields),
              };
              this.party = {
                ...this.party,
                ..._data,
              };
            }
          }),
        )
        .subscribe();
    }
  }

  getItemOptionName = (item: ResourceAssignedToFloorplan): string => {
    return this.getItemName(item, true);
  };

  getItemName(item: ResourceAssignedToFloorplan, addCapacity = false): string {
    let result = item?.name || '';

    if (addCapacity) {
      result += ` (${item.capacity.max}) `;
    }

    if (item?.onFloorplan) {
      result += ` [${item.onFloorplan}]`;
    }

    return result;
  }

  changeDuration() {
    this.party.duration = this.party.durationInMinutes * this.msPerMin;
    this.checkWorkTime();
  }

  isConfirmPartyButtonDisabled() {
    return (this.showOverlappingAlert || this.showResourcesSizeAlert);
  }

  changeDate() {
    if (
      this.selectedTimeMode ||
      !this.party.time ||
      !this.party.time.includes('M') ||
      typeof this.party.date === 'string' ||
      this.party.date === null
    ) {
      return false;
    }

    this.currentTime = this.dateTimeService.setTimeToDate(
      this.dateTimeService.ngbDateToMoment(this.party.date, this.timezone),
      this.party.time,
      this.timezone,
    );

    if (this.servicesEnabled && this.onlineReservationEnabled && !this.resourcesEnabled) {
      this.clearServicesResources();
    }
    this.overrideIsWorkTime = false;
    this.checkWorkTime();
  }

  clean(obj) {
    for (const propName in obj) {
      if (propName === 'messagingProvider') {
        continue;
      }

      if (obj[propName] === null || obj[propName] === undefined) {
        delete obj[propName];
      }
    }
  }

  saveParty(): void {
    const party = { ...this.party };
    this.clean(party);
    if (party.phone) {
      party.phone = this.telService.format(party.phone, this.user.country, 'intl');
    }

    this.convertQuotedTime(party);
    if ((!this.isWorkTime || this.isPartyInPastTime) && !this.overrideIsWorkTime) {
      this.isSubmitting = false;

      return;
    }

    if (party.resource && !(party.resources || []).length) {
      if (!Array.isArray(party.resource)) {
        party.resources = [party.resource];
      } else {
        party.resources = party.resource;
      }
      delete party.resource;
    }
    party.overrideNoResources = this.overrideNoResources;

    if (!this.showOverlappingAlert && this.isEditMode) {
      party.overrideNoResources = true;
    }

    if (party.type !== PartyTypes.reservation && !Boolean(this.partyToEdit)) {
      party.checkin_time = party.checkin_time || Date.now();
    }

    this.savePartyAction(party);
  }

  cancel(): void {
    void this.modalController.dismiss(null);
  }

  ok(showSuccessUpdateAlert = true): void {
    const { phone } = this.party;
    if (phone !== '' && this.parties.find(party => party.phone === phone)) {
      this.modalController.create<unknown, string>(this.confirmModalContentRef.nativeElement)
        .pipe(
          take(1),
          tap((res) => {
            if (res === 'Cancel click') {
              return;
            }

            this.actionAfterCloseModal();
          }),
          catchError((error) => {
            this.cancel();

            return of(error);
          }),
        )
        .subscribe();

      return;
    }
    this.actionAfterCloseModal();
  }

  actionAfterCloseModal(): void {
    const { size, sizeOther } = this.party;
    if (this.user.waitFields.size &&
      size &&
      typeof size !== 'number' ||
      sizeOther &&
      typeof sizeOther !== 'number') {
      this.showSizeValidationMessage = true;

      return;
    }
    this.isSubmitting = true;
    this.saveParty();
  }

  isSubmitDisabled(addEditForm: NgForm): boolean {
    if (!addEditForm) {
      return;
    }

    const isAvailableTimeAndNoOverride = this.isAvailableTime && !this.overrideNoResources;
    const isOutOutBusinessHours = (!this.isWorkTime || this.isPartyInPastTime) && !this.overrideIsWorkTime;

    return addEditForm.form.invalid ||
      isOutOutBusinessHours ||
      this.noAvailableServices(addEditForm) ||
      this.isConfirmPartyButtonDisabled() ||
      addEditForm.form.pending ||
      this.isSubmitting ||
      isAvailableTimeAndNoOverride;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  undoCheckIn(): void {
    const party = cloneDeep(this.partyToEdit);
    party.checkin_time = null;
    party.overrideNoResources = this.overrideNoResources;

    if (this.isEditMode) {
      party.overrideNoResources = true;
    }

    this.savePartyAction(party, 'check-in was canceled');
  }

  undoServed(): void {
    this.isSubmitting = true;
    const options = {
      party_id: this.partyToEdit._id,
      seated_time: null,
      canceled_time: null,
    };

    this.markPartyService.setParty(options)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess(
            `moved to ${this.partyToEdit.type === PartyTypes.togo ? 'To Go' : 'waitlist'}`,
            'Success: ',
          );
          void this.modalController.dismiss();
          this.isSubmitting = false;
        }),
        catchError(err => {
          this.isSubmitting = false;
          this.alertsService.addError('an error occurred during moving party to Waitlist');

          return of(err);
        }),
      ).subscribe();
  }

  checkIfOutOfBusinessHours(addEditForm: NgForm): boolean {
    return this.isOutOfBusinessHours(addEditForm) && !this.overrideIsWorkTime;
  }

  setPartyDurationInMinutes() {
    this.party.durationInMinutes = Math.round(this.party.duration / this.msPerMin) || 0;
  }

  setShowResourcesSizeAlert() {
    if (!this.resourcesInDropdown?.length) {
      return;
    }

    this.showResourcesSizeAlert = true;
  }

  isWhatsAppEnabled() {
    return useWhatsApp(this.user);
  }

  isUsOrCaPhoneNumber(): boolean {
    return isUsOrCaPhoneNumber(this.party.phone);
  }

  changeMessagingProvider() {
    if (!this.party.phone || isUsOrCaPhoneNumber(this.party.phone)) {
      this.party.messagingProvider = null;
    }
  }

  private savePartyAction(party: IParty, successMessage = 'updated successfully'): void {
    if (this.isSavingParty) {
      return;
    }

    this.isSubmitting = true;
    this.isSavingParty = true;
    this.partiesService.saveParty(party)
      .pipe(
        take(1),
        tap((data) => {
          void this.modalController.dismiss(data);
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Success:',
            body: successMessage,
          });
        }),
        catchError((err) => {
          if (err?.reloadData) {
            this.party.resources = [];
            this.checkWorkTime();
          }

          const customer = this.user.displayOptions?.labels?.customer || 'customer';
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `new ${customer} not added successfully: ${err?.message}`,
          });

          if (this.resourceOverrideCallback) {
            this.resourceOverrideCallback(this.party);
          }

          return of(err);
        }),
        finalize(() => {
          this.isSavingParty = false;
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  private setDeterminerValues(delta: number): void {
    this.deltaResourcesSize = delta;
    if (delta > 0) {
      this.determiner = 'more';
      this.determinerChange = 'decrease';
      this.determinerFix = RESOURCE_ADJUSTMENT_ITEMS.add;

      return;
    }
    this.determiner = 'less';
    this.determinerChange = 'increase';
    this.determinerFix = RESOURCE_ADJUSTMENT_ITEMS.remove;
  }
}
